<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useReveal } from '../composables/useReveal'

const { t } = useI18n()
const { elementRef: sectionRef, isVisible } = useReveal(0.1)

const solutions = ['amazon', 'consolidation', 'visibility', 'ecosystem']
</script>

<template>
  <section id="solutions" ref="sectionRef" class="section section-dark solutions">
    <div class="solutions__glow" />

    <div class="container solutions__inner">
      <div class="solutions__header">
        <div :class="['eyebrow', 'reveal', { visible: isVisible }]">{{ t('solutions.eyebrow') }}</div>
        <h2 :class="['reveal', 'reveal-delay-1', { visible: isVisible }]">{{ t('solutions.title') }}</h2>
        <p :class="['reveal', 'reveal-delay-2', { visible: isVisible }]">{{ t('solutions.subtitle') }}</p>
      </div>

      <div class="solutions__grid">
        <article
          v-for="(solution, index) in solutions"
          :key="solution"
          :class="['solution-card', 'reveal', `reveal-delay-${Math.min(index + 1, 4)}`, { visible: isVisible }]"
        >
          <div class="solution-card__index">0{{ index + 1 }}</div>
          <h3>{{ t(`solutions.items.${solution}.title`) }}</h3>
          <p>{{ t(`solutions.items.${solution}.desc`) }}</p>
        </article>
      </div>
    </div>
  </section>
</template>

<style scoped>
.solutions {
  position: relative;
  overflow: hidden;
}

.solutions__glow {
  position: absolute;
  inset: 0;
  background:
    radial-gradient(ellipse 60% 50% at 20% 50%, rgba(13, 115, 119, 0.18), transparent 60%),
    radial-gradient(ellipse 50% 40% at 80% 80%, rgba(196, 92, 62, 0.1), transparent 60%);
  pointer-events: none;
}

.solutions__inner {
  position: relative;
  z-index: 2;
}

.solutions__header {
  text-align: center;
  margin-bottom: 5rem;
}

.solutions__header h2 {
  margin-bottom: 1.25rem;
}

.solutions__header p {
  max-width: 640px;
  margin-inline: auto;
}

.solutions__grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
}

.solution-card {
  padding: 3rem;
  border-radius: var(--radius-lg);
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  transition: background var(--duration-fast) var(--ease-out-expo),
    border-color var(--duration-fast) var(--ease-out-expo),
    transform var(--duration-fast) var(--ease-out-expo);
}

.solution-card:hover {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.15);
  transform: translateY(-4px);
}

.solution-card__index {
  font-family: var(--font-display);
  font-size: 1rem;
  font-weight: 700;
  color: var(--color-accent-light);
  margin-bottom: 1.5rem;
  letter-spacing: 0.1em;
}

.solution-card h3 {
  margin-bottom: 1rem;
  font-size: 1.625rem;
}

.solution-card p {
  color: var(--color-text-tertiary);
  line-height: 1.75;
}

@media (max-width: 768px) {
  .solutions__grid {
    grid-template-columns: 1fr;
  }

  .solution-card {
    padding: 2rem;
  }
}
</style>
